'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@extern/ui/button';
import { Checkbox } from '@extern/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@extern/ui/form';
import { Input } from '@extern/ui/input';
import { InputPassword, InputPasswordTooltip } from '@extern/ui/input-password';
import { Separator } from '@extern/ui/separator';
import { Text } from '@extern/ui/text';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { AuthCard } from '@/app/(auth)/_partials/auth-card';
import { AuthGoogleButton } from '@/app/(auth)/_partials/auth-google-button';
import { AuthLastUsedButtonWrap } from '@/app/(auth)/_partials/auth-last-used-badge';
import { emailSchema, passwordSchema } from '@/infra/schemas/auth';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';
import { useLastAuthenticationStrategy } from '../../_hooks/use-last-authentication-strategy';
import { useSignUp } from '../_hooks/use-sign-up';
import { useSignUpFlowStore } from '../_hooks/use-sign-up-flow-store';
const FormSchema = z.object({
  firstName: z.string().min(2, {
    message: 'First name is required'
  }),
  lastName: z.string().min(2, {
    message: 'Last name is required'
  }),
  emailAddress: emailSchema,
  password: passwordSchema
});
export function SignUpFormControl() {
  const $ = _c(59);
  const searchParams = useSearchParams();
  const {
    onSignUpSubmit,
    onSignUpWithGoogle,
    isPending,
    isGoogleLoading
  } = useSignUp();
  const {
    trackSignUpChoiceClicked
  } = useAuthEvents();
  const lastAuthenticationStrategy = useLastAuthenticationStrategy();
  const newsletter = useSignUpFlowStore(_temp);
  const onChangeFlow = useSignUpFlowStore(_temp2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      resolver: zodResolver(FormSchema),
      defaultValues: {
        firstName: "",
        lastName: "",
        emailAddress: "",
        password: ""
      },
      mode: "onChange"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const form = useForm(t0);
  let t1;
  if ($[1] !== newsletter || $[2] !== onSignUpSubmit || $[3] !== trackSignUpChoiceClicked) {
    t1 = function handleSubmit(values) {
      trackSignUpChoiceClicked("email", newsletter);
      onSignUpSubmit({
        ...values,
        newsletter
      });
    };
    $[1] = newsletter;
    $[2] = onSignUpSubmit;
    $[3] = trackSignUpChoiceClicked;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleSubmit = t1;
  let t2;
  if ($[5] !== form || $[6] !== handleSubmit) {
    t2 = form.handleSubmit(handleSubmit);
    $[5] = form;
    $[6] = handleSubmit;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <AuthCard.Header><AuthCard.Title>Start Your Career Journey with Extern</AuthCard.Title><AuthCard.Description>Gain access to real-world projects, hands-on experience, and a community of professionals.</AuthCard.Description></AuthCard.Header>;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[9] !== form.control) {
    t4 = <FormField control={form.control} name="firstName" render={_temp3} />;
    t5 = <FormField control={form.control} name="lastName" render={_temp4} />;
    t6 = <FormField control={form.control} name="emailAddress" render={_temp5} />;
    t7 = <FormField control={form.control} name="password" render={_temp6} />;
    $[9] = form.control;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
  }
  let t8;
  if ($[14] !== onChangeFlow) {
    t8 = checked => onChangeFlow({
      newsletter: checked === true
    });
    $[14] = onChangeFlow;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== newsletter || $[17] !== t8) {
    t9 = <div className="flex gap-2"><Checkbox checked={newsletter} label="I want exclusive invites and opportunities at Extern" onCheckedChange={t8} /></div>;
    $[16] = newsletter;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== t9) {
    t10 = <AuthCard.Content><div className="grid gap-4">{t4}{t5}{t6}{t7}{t9}</div></AuthCard.Content>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <br />;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Link className="underline" href="https://www.extern.com/terms-of-service" target="_blank">Terms</Link>;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="text-center font-medium text-gray-dark text-xs">By signing up, you agree to{t11}Extern's{" "}{t12}{" "}and{" "}<Link className="underline" href="https://www.extern.com/privacy-policy" target="_blank">Privacy Policy</Link>.</div>;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== isPending) {
    t14 = <Button className="w-full" disabled={isPending} isLoading={isPending} type="submit">Sign up with Email</Button>;
    $[28] = isPending;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== lastAuthenticationStrategy || $[31] !== t14) {
    t15 = <AuthLastUsedButtonWrap matchStrategy="password" strategy={lastAuthenticationStrategy}>{t14}</AuthLastUsedButtonWrap>;
    $[30] = lastAuthenticationStrategy;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="relative flex w-full items-center justify-center p-3"><Separator /><span className="absolute mx-auto bg-white px-4 text-gray-dark text-sm">or</span></div>;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== newsletter || $[35] !== onSignUpWithGoogle || $[36] !== trackSignUpChoiceClicked) {
    t17 = () => {
      trackSignUpChoiceClicked("google", newsletter);
      onSignUpWithGoogle(newsletter).catch(_temp7);
    };
    $[34] = newsletter;
    $[35] = onSignUpWithGoogle;
    $[36] = trackSignUpChoiceClicked;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== isGoogleLoading || $[39] !== t17) {
    t18 = <AuthGoogleButton isLoading={isGoogleLoading} label="Sign up with Google" onClick={t17} />;
    $[38] = isGoogleLoading;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== searchParams) {
    t19 = getAllowlistedSearchParams(searchParams);
    $[41] = searchParams;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  const t20 = `/sign-in?${t19}`;
  let t21;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <span className="text-blue-dark hover:underline">Sign in</span>;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t20) {
    t22 = <Text body="lg" className="text-center font-medium">Already have an account? <Link href={t20}>{t21}</Link></Text>;
    $[44] = t20;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t15 || $[47] !== t18 || $[48] !== t22) {
    t23 = <AuthCard.Footer>{t13}{t15}{t16}{t18}{t22}</AuthCard.Footer>;
    $[46] = t15;
    $[47] = t18;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t10 || $[51] !== t23) {
    t24 = <AuthCard>{t3}{t10}{t23}</AuthCard>;
    $[50] = t10;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t2 || $[54] !== t24) {
    t25 = <form onSubmit={t2}>{t24}</form>;
    $[53] = t2;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== form || $[57] !== t25) {
    t26 = <Form {...form}>{t25}</Form>;
    $[56] = form;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  return t26;
}
function _temp7() {}
function _temp6(t0) {
  const {
    field: field_2
  } = t0;
  return <FormItem><FormLabel className="flex items-center gap-2">Create a password<InputPasswordTooltip value={field_2.value} /></FormLabel><FormControl><InputPassword autoCapitalize="none" autoComplete="password" autoCorrect="off" data-cy="password" id="password" key="password" placeholder="Type in your password" type="password" {...field_2} /></FormControl><FormMessage /></FormItem>;
}
function _temp5(t0) {
  const {
    field: field_1
  } = t0;
  return <FormItem><FormLabel>Email</FormLabel><FormControl><Input placeholder="email@service.com" {...field_1} /></FormControl><FormMessage /></FormItem>;
}
function _temp4(t0) {
  const {
    field: field_0
  } = t0;
  return <FormItem><FormLabel>Last name</FormLabel><FormControl><Input placeholder="Last name" {...field_0} /></FormControl><FormMessage /></FormItem>;
}
function _temp3(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormLabel>First name</FormLabel><FormControl><Input placeholder="First name" {...field} /></FormControl><FormMessage /></FormItem>;
}
function _temp2(s_0) {
  return s_0.onChange;
}
function _temp(s) {
  return s.newsletter;
}